import { motion, useReducedMotion } from 'framer-motion'
import { CheckCircle, Clock, DollarSign, Receipt, TrendingDown } from 'lucide-react'
import {
  OPERACAO_METRIC_ACCENTS,
  OperacaoMetricCard,
} from '@/components/Dashboard/OperacaoMetricCard'
import { getDashboardVariants } from '@/components/Dashboard/dashboardMotion'
import type { CommissionsTabView } from '@/components/Dashboard/commissionsTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'

type Props = {
  view: CommissionsTabView
  valuesVisible?: boolean
}

export function CommissionsKpiGrid({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const v = getDashboardVariants(reduced)

  const cards = [
    {
      key: 'pending',
      label: 'A pagar',
      value: formatDashboardCurrency(view.totalPending, valuesVisible),
      numericValue: view.totalPending,
      valueFormat: 'currency' as const,
      subtitle: 'Comissões pendentes no período',
      icon: <Clock size={16} />,
      accent: { bg: '#fef3c7', text: '#d97706', ring: '#fcd34d' },
    },
    {
      key: 'paid',
      label: 'Pagas',
      value: formatDashboardCurrency(view.totalPaid, valuesVisible),
      numericValue: view.totalPaid,
      valueFormat: 'currency' as const,
      subtitle: 'Já liquidadas no período',
      icon: <CheckCircle size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.closed,
    },
    {
      key: 'total',
      label: 'Volume total',
      value: formatDashboardCurrency(view.totalValue, valuesVisible),
      numericValue: view.totalValue,
      valueFormat: 'currency' as const,
      subtitle: `${formatDashboardNumber(view.totalCount, valuesVisible)} registros`,
      icon: <DollarSign size={16} />,
      accent: OPERACAO_METRIC_ACCENTS.pipeline,
    },
    {
      key: 'count',
      label: 'Registros',
      value: formatDashboardNumber(view.totalCount, valuesVisible),
      numericValue: view.totalCount,
      valueFormat: 'integer' as const,
      subtitle:
        view.totalReversed > 0
          ? `${formatDashboardCurrency(view.totalReversed, valuesVisible)} estornadas`
          : 'Deals ganhos no período',
      icon: view.totalReversed > 0 ? <TrendingDown size={16} /> : <Receipt size={16} />,
      accent: { bg: '#ede9fe', text: '#7c3aed', ring: '#c4b5fd' },
    },
  ]

  return (
    <motion.div
      variants={v.kpiGrid}
      initial='hidden'
      animate='show'
      className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'
    >
      {cards.map((kpi, index) => (
        <motion.div key={kpi.key} variants={v.kpiItem} className='min-h-[118px]'>
          <OperacaoMetricCard
            label={kpi.label}
            value={kpi.value}
            numericValue={kpi.numericValue}
            valueFormat={kpi.valueFormat}
            subtitle={kpi.subtitle}
            trendPositive={kpi.key === 'paid' ? view.totalPaid > 0 : view.totalCount > 0}
            icon={kpi.icon}
            accent={kpi.accent}
            valuesVisible={valuesVisible}
            animationDelay={index * 90}
            variant='compact'
          />
        </motion.div>
      ))}
    </motion.div>
  )
}
